import {
  Action,
  ElectronicSignatureProvider,
  PathsV1VehicleBuyInUuidGetParametersQueryExtend,
  VehicleBuyInStatus,
} from "@/api-schema-enums";
import { Can, useAbility } from "@/casl-provider";
import {
  type VehicleBuyInByUuidParameters,
  type VehicleBuyInByUuidResponse,
  useVehicleBuyInByUuidQuery,
} from "@/common/api/vehicle-buy-in/use-vehicle-buy-in-by-uuid.query";
import { useVehicleBuyInCancelMutation } from "@/common/api/vehicle-buy-in/use-vehicle-buy-in-cancel.mutation";
import { useVehicleBuyInRefreshStatusMutation } from "@/common/api/vehicle-buy-in/use-vehicle-buy-in-refresh-status.mutation";
import { useVehicleBuyInSyncV1GewaMutation } from "@/common/api/vehicle-buy-in/use-vehicle-buy-in-sync-v1-gewa.mutation";
import { openInNewTab } from "@/common/helpers/open-in-new-tab";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { useAppFeatureFlag } from "@/common/hooks/use-app-feature-flag";
import { BaseConfirmDialog } from "@/components/confirm-dialog/base-confirm-dialog";
import { DetailActions } from "@/components/detail-page/detail-actions";
import type { DetailAction } from "@/components/detail-page/types";
import { CenteredLayout } from "@/components/layouts/centered-layout";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ButtonColorVariant } from "@/components/ui/button";
import { UuidExistenceWrapper } from "@/components/uuid-existence-wrapper/uuid-existence-wrapper";
import { FeatureFlag } from "@/config/feature-flags";
import {
  canSyncVehicleBuyInToV1,
  getOpenableSignedDocumentUrl,
  isVehicleBuyInCancellationAvailable,
  resolveVehicleBuyInV1SyncErrorMessage,
} from "@/modules/fab/vehicle-buy-in/_helpers/vehicle-buy-in-detail.helpers";
import { getVehicleValuationRoute } from "@/modules/fab/vehicle-valuation/_helpers/get-vehicle-valuation-route";
import { CircleXIcon, ExternalLinkIcon, EyeIcon, RefreshCwIcon, TriangleAlertIcon } from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "wouter";
import { VehicleBuyInSnapshotDetails } from "./_components/vehicle-buy-in-snapshot-details";

const VEHICLE_BUY_IN_REFETCH_INTERVAL_MS = 10_000;

export function VehicleBuyInDetailView() {
  const { t } = useTranslation("fab");
  const ability = useAbility();
  const [, navigate] = useLocation();
  const { value: isVehicleBuyInV1SyncEnabled } = useAppFeatureFlag(FeatureFlag.IsVehicleBuyInV1SyncEnabled);
  const params = useParams<{ uuid: string }>();
  const uuid = params.uuid ?? "";
  const cancelMutation = useVehicleBuyInCancelMutation();
  const refreshStatusMutation = useVehicleBuyInRefreshStatusMutation();
  const syncV1GewaMutation = useVehicleBuyInSyncV1GewaMutation();
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [isV1ResyncDialogOpen, setIsV1ResyncDialogOpen] = useState(false);
  const isSyncingV1Ref = useRef(false);
  const vehicleBuyInQuery = useVehicleBuyInByUuidQuery(
    uuid,
    {
      parameters: {
        extend: [
          PathsV1VehicleBuyInUuidGetParametersQueryExtend.Vehicle,
          PathsV1VehicleBuyInUuidGetParametersQueryExtend.VehicleValuation,
          PathsV1VehicleBuyInUuidGetParametersQueryExtend.ElectronicSignature,
          PathsV1VehicleBuyInUuidGetParametersQueryExtend.Branch,
          PathsV1VehicleBuyInUuidGetParametersQueryExtend.CreatedBy,
        ],
      },
    },
    { refetchInterval: VEHICLE_BUY_IN_REFETCH_INTERVAL_MS },
  );
  const signedDocumentUrl = vehicleBuyInQuery.data
    ? getOpenableSignedDocumentUrl(vehicleBuyInQuery.data.status, vehicleBuyInQuery.data.electronicSignature)
    : undefined;
  const electronicSignature = vehicleBuyInQuery.data?.electronicSignature;
  const canRefreshPaperlessStatus =
    electronicSignature?.provider === ElectronicSignatureProvider.Paperless &&
    Boolean(electronicSignature.providerDocumentId?.trim());
  const vehicleBuyInStatus = vehicleBuyInQuery.data?.status;
  const canCancelVehicleBuyIn =
    vehicleBuyInStatus !== undefined &&
    ability.can(Action.Update, "VehicleBuyIn") &&
    isVehicleBuyInCancellationAvailable(vehicleBuyInStatus);
  const isCancellationRetry = vehicleBuyInStatus === VehicleBuyInStatus.ContractCancellationFailed;

  const showV1SyncAction = vehicleBuyInQuery.data
    ? canSyncVehicleBuyInToV1(
        vehicleBuyInQuery.data.status,
        ability.can("create", "VehicleBuyIn"),
        isVehicleBuyInV1SyncEnabled,
      )
    : false;
  const isV1Resync = vehicleBuyInQuery.data?.status === VehicleBuyInStatus.Completed;
  const v1SyncActionLabel = isV1Resync
    ? t("VehicleBuyInV1ResyncAction")
    : t(
        vehicleBuyInQuery.data?.status === VehicleBuyInStatus.V1SyncFailed
          ? "VehicleBuyInV1RetryAction"
          : "VehicleBuyInV1SyncAction",
      );
  const persistedV1SyncError =
    vehicleBuyInQuery.data?.status === VehicleBuyInStatus.V1SyncFailed
      ? vehicleBuyInQuery.data.errorMessage?.trim()
      : undefined;

  async function handleRefreshStatus(): Promise<void> {
    await toastMutationStatus({
      mutationFn: () => refreshStatusMutation.mutateAsync({ uuid }),
      successMessage: t("VehicleBuyInRefreshStatusSuccess"),
      errorMessage: t("VehicleBuyInRefreshStatusError"),
    });
  }

  async function handleCancelVehicleBuyIn(): Promise<void> {
    setCancelDialogOpen(false);
    await toastMutationStatus({
      mutationFn: () => cancelMutation.mutateAsync({ uuid }),
      successMessage: t("VehicleBuyInCancellationStarted"),
      errorMessage: t("VehicleBuyInCancellationError"),
    });
  }

  const handleSyncV1Gewa = async () => {
    if (!uuid || isSyncingV1Ref.current) return;

    isSyncingV1Ref.current = true;
    try {
      await toastMutationStatus({
        mutationFn: () => syncV1GewaMutation.mutateAsync({ uuid }),
        successMessage: t("VehicleBuyInV1SyncSuccess"),
        errorMessage: t("VehicleBuyInV1SyncError"),
        resolveErrorMessage: resolveVehicleBuyInV1SyncErrorMessage,
      });
    } finally {
      isSyncingV1Ref.current = false;
    }
  };

  const handleV1SyncAction = () => {
    if (isV1Resync) {
      setIsV1ResyncDialogOpen(true);
      return;
    }

    void handleSyncV1Gewa();
  };

  const handleConfirmV1Resync = () => {
    setIsV1ResyncDialogOpen(false);
    void handleSyncV1Gewa();
  };

  const vehicleValuation = vehicleBuyInQuery.data?.vehicleValuation;
  const headerActionCandidates: (DetailAction | false)[] = [
    !!vehicleValuation && {
      id: "vehicle-buy-in-view-valuation",
      label: t("ViewVehicleValuation"),
      icon: EyeIcon,
      permission: { action: Action.Read, subject: "VehicleValuation" },
      onClick: () => navigate(getVehicleValuationRoute(vehicleValuation)),
    },
    !!signedDocumentUrl && {
      id: "vehicle-buy-in-open-signed-document",
      label: t("OpenSignedDocument"),
      icon: ExternalLinkIcon,
      onClick: () => openInNewTab(signedDocumentUrl),
    },
    canRefreshPaperlessStatus && {
      id: "vehicle-buy-in-refresh-status",
      label: t("VehicleBuyInRefreshStatus"),
      icon: RefreshCwIcon,
      menuOnly: true,
      disabled: refreshStatusMutation.isPending,
      onClick: () => void handleRefreshStatus(),
    },
    showV1SyncAction && {
      id: "vehicle-buy-in-sync-v1",
      label: v1SyncActionLabel,
      icon: RefreshCwIcon,
      menuOnly: true,
      disabled: syncV1GewaMutation.isPending,
      onClick: handleV1SyncAction,
    },
    canCancelVehicleBuyIn && {
      id: "vehicle-buy-in-cancel",
      label: t(isCancellationRetry ? "VehicleBuyInRetryCancellation" : "VehicleBuyInCancel"),
      icon: CircleXIcon,
      menuOnly: true,
      destructive: true,
      disabled: cancelMutation.isPending,
      onClick: () => setCancelDialogOpen(true),
    },
  ];
  const headerActions = headerActionCandidates.filter((action): action is DetailAction => !!action);

  return (
    <Can I="read" a="VehicleBuyIn">
      <UuidExistenceWrapper<VehicleBuyInByUuidParameters, VehicleBuyInByUuidResponse>
        uuid={uuid}
        query={useVehicleBuyInByUuidQuery}
        queryResult={vehicleBuyInQuery}
        notFoundProps={{
          title: t("VehicleBuyInNotFound"),
          message: t("RequestedVehicleBuyInDoesNotExist"),
          description: t("PleaseCheckVehicleBuyInIdOrContactSupport"),
          backTo: "~/fab/buy-in",
          backToLabel: t("BackToVehicleBuyIns"),
        }}
      >
        <CenteredLayout title={t("VehicleBuyIn")} subTitle={t("VehicleBuyInDetailsDescription")}>
          <div className="flex justify-end">
            <DetailActions actions={headerActions} />
          </div>
          {vehicleBuyInQuery.data ? (
            <div className="space-y-4">
              {persistedV1SyncError ? (
                <Alert variant="error">
                  <TriangleAlertIcon />
                  <AlertTitle>{t("VehicleBuyInV1SyncErrorTitle")}</AlertTitle>
                  <AlertDescription>{persistedV1SyncError}</AlertDescription>
                </Alert>
              ) : null}
              <VehicleBuyInSnapshotDetails vehicleBuyIn={vehicleBuyInQuery.data} />
            </div>
          ) : null}

          <BaseConfirmDialog
            open={cancelDialogOpen}
            title={t("VehicleBuyInCancelDialogTitle")}
            description={t("VehicleBuyInCancelDialogDescription")}
            labelCancel={t("common:Cancel")}
            labelConfirm={t("VehicleBuyInCancelConfirm")}
            confirmColorVariant={ButtonColorVariant.Error}
            onCancel={() => setCancelDialogOpen(false)}
            onConfirm={() => void handleCancelVehicleBuyIn()}
          />
          <BaseConfirmDialog
            open={isV1ResyncDialogOpen}
            title={t("VehicleBuyInV1ResyncConfirmTitle")}
            description={t("VehicleBuyInV1ResyncConfirmDescription")}
            labelConfirm={t("VehicleBuyInV1ResyncAction")}
            onCancel={() => setIsV1ResyncDialogOpen(false)}
            onConfirm={handleConfirmV1Resync}
          />
        </CenteredLayout>
      </UuidExistenceWrapper>
    </Can>
  );
}
